// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
export interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class BreadcrumbComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];
  isVisible: boolean = false;

  constructor(private router: Router, @Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.generateBreadcrumbs();
    });

    // Initial check
    this.generateBreadcrumbs();
  }

  generateBreadcrumbs(): void {
    const currentRoute = this.router.url;

    // Hide on home page
    if (currentRoute === '/' || currentRoute === '/home') {
      this.isVisible = false;
      this.removeBreadcrumbSchema();
      return;
    }

    this.isVisible = true;
    this.breadcrumbs = [];

    // Always start with Home
    this.breadcrumbs.push({ label: 'Home', url: '/' });

    // Process path segments
    const segments = currentRoute.split('/').filter(segment => segment !== '');
    let accumulatedUrl = '';

    segments.forEach(segment => {
      accumulatedUrl += `/${segment}`;

      // Format label: remove queries, replace hyphens with spaces, capitalize
      let label = segment.split('?')[0];
      label = label.replace(/-/g, ' ');
      label = this.toTitleCase(label);

      this.breadcrumbs.push({
        label: label,
        url: accumulatedUrl
      });
    });

    this.injectBreadcrumbSchema();
  }

  private injectBreadcrumbSchema(): void {
    this.removeBreadcrumbSchema();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: this.breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: environment.siteUrl + crumb.url
      }))
    };

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.className = 'breadcrumb-json-ld';
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  private removeBreadcrumbSchema(): void {
    this.document.head.querySelector('script.breadcrumb-json-ld')?.remove();
  }

  private toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
}
